import { type Dayjs } from 'dayjs';
import { type ReactElement, useCallback, useMemo, useState } from 'react';
import { formatRangeWindowNoDataText } from '@/features/dashboard/hooks';
import { PipelinesTableClone } from '@/features/pipelines/components/PipelinesTableClone';
import { queryPipelines } from '@/features/pipelines/data';
import {
  PipelinesCloneToolbar,
  type CloneDateQuickFilter,
} from '@/features/pipelines/pages/PipelinesCloneToolbar';
import {
  cloneSortFieldMap,
  type PipelineCloneStatus,
  type CloneSortDirection,
  type CloneSortKey,
  cloneDefaultSortKey,
  cloneDefaultSortDirection,
  cloneDefaultColumnVisibilityModel,
} from '@/features/pipelines/pages/pipelinesPageCloneConfig';
import { useCloneDateWindow } from '@/features/shared/hooks/useCloneDateWindow';
import { resolveEntityTags } from '@/features/shared/tags';
import {
  buildInFilter,
  buildInFilterWithNull,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import { Panel } from '@/ui/composites/Panel';
import type { ApiSort, GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { GridColumnVisibilityModel } from '@/ui/primitives';
import type { PipelineListItemDto, QueryResult } from '@flowqa/shared';

type PipelinesClonePanelProps = {
  visible: boolean;
  selectedProjectId: string | null;
  selectedOrganisationId: string | null;
  deltasEnabled: boolean;
};

export const PipelinesClonePanel = ({
  visible,
  selectedProjectId,
  selectedOrganisationId,
  deltasEnabled,
}: PipelinesClonePanelProps): ReactElement | null => {
  const [cloneDateQuickFilter, setCloneDateQuickFilter] = useState<CloneDateQuickFilter>('7d');
  const [cloneFromDate, setCloneFromDate] = useState<Dayjs | null>(null);
  const [cloneToDate, setCloneToDate] = useState<Dayjs | null>(null);
  const [debouncedCloneSearch, setDebouncedCloneSearch] = useState('');
  const [cloneColumnVisibilityModel, setCloneColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(cloneDefaultColumnVisibilityModel);
  const [cloneSortKey, setCloneSortKey] = useState<CloneSortKey>(cloneDefaultSortKey);
  const [cloneSortDirection, setCloneSortDirection] =
    useState<CloneSortDirection>(cloneDefaultSortDirection);
  const [cloneFilterStatuses, setCloneFilterStatuses] = useState<PipelineCloneStatus[]>([]);
  const [cloneFilterTags, setCloneFilterTags] = useState<string[]>([]);
  const [cloneVisibleRows, setCloneVisibleRows] = useState<PipelineListItemDto[]>([]);

  const cloneDateWindow = useCloneDateWindow({
    dateQuickFilter: cloneDateQuickFilter,
    fromDate: cloneFromDate,
    toDate: cloneToDate,
  });
  const cloneAvailableTags = useMemo(() => {
    const tags = new Set<string>();
    cloneVisibleRows.forEach((row) => {
      resolveEntityTags(row).forEach((tag) => {
        tags.add(tag);
      });
    });
    return Array.from(tags.values()).sort((left, right) => left.localeCompare(right));
  }, [cloneVisibleRows]);
  const handleDebouncedCloneSearchChange = useCallback((value: string) => {
    setDebouncedCloneSearch(value);
  }, []);
  const cloneBuildStatusFilters = useMemo<GridQueryInput['filters']>(() => {
    if (cloneFilterStatuses.length === 0) return [];
    const hasNoBuilds = cloneFilterStatuses.includes('none');
    const buildStatuses = cloneFilterStatuses.filter(
      (status): status is Exclude<PipelineCloneStatus, 'none'> => status !== 'none',
    );
    const values = hasNoBuilds
      ? ([null, ...buildStatuses] as Array<Exclude<PipelineCloneStatus, 'none'> | null>)
      : buildStatuses;
    return buildInFilterWithNull('latestBuildStatus', values);
  }, [cloneFilterStatuses]);
  const cloneQueryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<PipelineListItemDto>> => {
      const toolbarFilters = mergeFilters(
        cloneBuildStatusFilters,
        buildInFilter('tags', cloneFilterTags),
      );
      const sortOverride: ApiSort = {
        field: cloneSortFieldMap[cloneSortKey],
        direction: cloneSortDirection,
      };
      return queryPipelines(selectedProjectId, selectedOrganisationId ?? undefined, {
        ...input,
        sorts: [sortOverride],
        filters: [...input.filters, ...toolbarFilters],
        metricsStartDay: cloneDateWindow.startDay,
        metricsEndDay: cloneDateWindow.endDay,
        previousMetricsStartDay: cloneDateWindow.previousStartDay,
        previousMetricsEndDay: cloneDateWindow.previousEndDay,
        include: {
          latestBuild: true,
          metrics: true,
          metricDeltas: deltasEnabled,
        },
      });
    },
    [
      cloneBuildStatusFilters,
      cloneDateWindow.endDay,
      cloneDateWindow.previousEndDay,
      cloneDateWindow.previousStartDay,
      cloneDateWindow.startDay,
      cloneFilterTags,
      cloneSortDirection,
      cloneSortKey,
      deltasEnabled,
      selectedOrganisationId,
      selectedProjectId,
    ],
  );
  const cloneNoRowsText = cloneDateWindow.hasCustomBounds
    ? 'No pipelines found for selected date range'
    : formatRangeWindowNoDataText('No pipelines found', cloneDateQuickFilter, false);
  const cloneStatusFilterKey = useMemo(
    () => [...cloneFilterStatuses].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneFilterStatuses],
  );
  const cloneTagFilterKey = useMemo(
    () => [...cloneFilterTags].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneFilterTags],
  );

  if (!visible) return null;

  return (
    <Panel padding="md" gap="none" contentGap="md" sx={{ backgroundColor: 'background.paper' }}>
      <PipelinesCloneToolbar
        onDebouncedCloneSearchChange={handleDebouncedCloneSearchChange}
        columnVisibilityModel={cloneColumnVisibilityModel}
        onColumnVisibilityModelChange={setCloneColumnVisibilityModel}
        sortKey={cloneSortKey}
        sortDirection={cloneSortDirection}
        onSortChange={(nextSortKey, nextSortDirection) => {
          setCloneSortKey(nextSortKey);
          setCloneSortDirection(nextSortDirection);
        }}
        filterStatuses={cloneFilterStatuses}
        onFilterStatusesChange={setCloneFilterStatuses}
        filterTags={cloneFilterTags}
        onFilterTagsChange={setCloneFilterTags}
        dateQuickFilter={cloneDateQuickFilter}
        onDateQuickFilterChange={(next) => {
          setCloneDateQuickFilter(next);
          setCloneFromDate(null);
          setCloneToDate(null);
        }}
        fromDate={cloneFromDate}
        toDate={cloneToDate}
        onFromDateChange={setCloneFromDate}
        onToDateChange={setCloneToDate}
        availableTags={cloneAvailableTags}
      />
      <PipelinesTableClone
        persistKey="pipelines-table-clone"
        queryKey={[
          'pipelines-table-clone',
          cloneDateQuickFilter,
          selectedOrganisationId ?? null,
          selectedProjectId ?? null,
          deltasEnabled ? 'deltas-on' : 'deltas-off',
          cloneSortKey,
          cloneSortDirection,
          cloneStatusFilterKey,
          cloneTagFilterKey,
          cloneDateWindow.startDay,
          cloneDateWindow.endDay,
        ]}
        queryFn={cloneQueryFn}
        pageSizeOptions={[10]}
        initialPageSize={10}
        disablePageSizeSelect
        showDeltas={deltasEnabled}
        noRowsText={cloneNoRowsText}
        showToolbar={false}
        disableColumnSorting
        disableColumnMenu
        search={debouncedCloneSearch}
        onRowsChange={setCloneVisibleRows}
        columnVisibilityModel={cloneColumnVisibilityModel}
        onColumnVisibilityModelChange={(nextModel) => {
          setCloneColumnVisibilityModel({
            ...nextModel,
            name: true,
          });
        }}
      />
    </Panel>
  );
};
